import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Microphone capture: an audio blob for the record, and a live amplitude
 * signal for the waveform.
 *
 * The amplitude loop deliberately writes to a ref rather than to React state.
 * A capture runs for tens of seconds at 60fps; re-rendering the capture tree
 * on every frame is the difference between a control that feels instant and
 * one that stutters exactly when the parent is watching it. Consumers read
 * `getLevel()` inside their own animation frame and write to the DOM directly.
 *
 * Teardown is idempotent and exhaustive. A microphone left hot after a capture
 * is the one failure this component is never allowed to have.
 */

export type RecorderStatus = 'idle' | 'recording' | 'denied' | 'unavailable'

export interface AudioRecorder {
  /** False when the browser has no microphone API at all. */
  supported: boolean
  status: RecorderStatus
  /** Parent-facing message. Never blames the parent, always offers the way out. */
  error: string | null
  /** Live amplitude, 0..1, smoothed. Read inside an animation frame. */
  getLevel: () => number
  /** Milliseconds since this capture started. */
  getDurationMs: () => number
  start: () => Promise<boolean>
  stop: () => Promise<{ blob: Blob | null; durationMs: number }>
  cancel: () => void
}

const DENIED =
  'Microphone access is off for this site. You can type your note instead — it counts the same.'
const UNREACHABLE =
  'The microphone is busy or unavailable. You can type your note instead — it counts the same.'

function pickMimeType(): string | undefined {
  const MR = (window as any).MediaRecorder
  if (!MR?.isTypeSupported) return undefined
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
  return candidates.find((t) => MR.isTypeSupported(t))
}

export function useAudioRecorder(): AudioRecorder {
  const [supported] = useState(
    () => typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
  )
  const [status, setStatus] = useState<RecorderStatus>(supported ? 'idle' : 'unavailable')
  const [error, setError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const rafRef = useRef<number | null>(null)
  const levelRef = useRef(0)
  const startedAtRef = useRef(0)
  const endedAtRef = useRef(0)

  const teardown = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    const rec = recorderRef.current
    if (rec) {
      rec.ondataavailable = null
      rec.onstop = null
      if (rec.state !== 'inactive') {
        try { rec.stop() } catch { /* already stopped */ }
      }
      recorderRef.current = null
    }
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    const ctx = ctxRef.current
    if (ctx && ctx.state !== 'closed') void ctx.close()
    ctxRef.current = null
    analyserRef.current = null
    levelRef.current = 0
  }, [])

  // Unmount, navigation, a crash in a sibling — the mic still closes.
  useEffect(() => teardown, [teardown])

  const start = useCallback(async (): Promise<boolean> => {
    if (!supported) {
      setError(UNREACHABLE)
      setStatus('unavailable')
      return false
    }

    teardown()
    setError(null)
    chunksRef.current = []
    startedAtRef.current = Date.now()
    endedAtRef.current = 0

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
    } catch (err) {
      const name = (err as { name?: string })?.name
      const denied = name === 'NotAllowedError' || name === 'SecurityError'
      setError(denied ? DENIED : UNREACHABLE)
      setStatus(denied ? 'denied' : 'unavailable')
      return false
    }
    streamRef.current = stream

    // The analyser is what makes the waveform honest. If it fails we still
    // record — a capture without a waveform beats no capture.
    try {
      const Ctx = window.AudioContext ?? (window as any).webkitAudioContext
      const ctx: AudioContext = new Ctx()
      ctxRef.current = ctx
      if (ctx.state === 'suspended') await ctx.resume()

      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      analyser.smoothingTimeConstant = 0.7
      ctx.createMediaStreamSource(stream).connect(analyser)
      analyserRef.current = analyser

      const samples = new Uint8Array(analyser.fftSize)
      const tick = () => {
        const a = analyserRef.current
        if (!a) return
        a.getByteTimeDomainData(samples)
        let sum = 0
        for (let i = 0; i < samples.length; i++) {
          const d = (samples[i]! - 128) / 128
          sum += d * d
        }
        const rms = Math.sqrt(sum / samples.length)
        // Scaled so ordinary indoor speech sits mid-range rather than pinned.
        const norm = Math.min(1, Math.max(0, rms * 3.4))
        // Rise fast so a syllable registers, fall slower so it stays readable.
        const prev = levelRef.current
        levelRef.current = norm > prev ? norm : prev + (norm - prev) * 0.25
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    } catch {
      analyserRef.current = null
    }

    try {
      const mimeType = pickMimeType()
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      }
      rec.start()
      recorderRef.current = rec
    } catch {
      // No MediaRecorder: the transcript is still the record of what happened.
      recorderRef.current = null
    }

    setStatus('recording')
    return true
  }, [supported, teardown])

  const stop = useCallback(async () => {
    const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0
    endedAtRef.current = Date.now()

    const rec = recorderRef.current
    let blob: Blob | null = null

    if (rec && rec.state !== 'inactive') {
      blob = await new Promise<Blob | null>((resolve) => {
        // Never hang the commit on the recorder flushing. The observation is
        // the transcript; the audio is a bonus we are happy to lose.
        const done = (value: Blob | null) => {
          clearTimeout(timer)
          resolve(value)
        }
        const timer = setTimeout(() => done(null), 400)
        rec.onstop = () => {
          const parts = chunksRef.current
          done(parts.length ? new Blob(parts, { type: rec.mimeType || 'audio/webm' }) : null)
        }
        try { rec.stop() } catch { done(null) }
      })
    }

    teardown()
    setStatus('idle')
    return { blob, durationMs }
  }, [teardown])

  const cancel = useCallback(() => {
    endedAtRef.current = Date.now()
    chunksRef.current = []
    teardown()
    setStatus('idle')
  }, [teardown])

  const getLevel = useCallback(() => levelRef.current, [])
  const getDurationMs = useCallback(
    () => (startedAtRef.current ? (endedAtRef.current || Date.now()) - startedAtRef.current : 0),
    [],
  )

  return { supported, status, error, getLevel, getDurationMs, start, stop, cancel }
}
